import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Building2, CheckCircle2, Loader2, ShieldAlert, XCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { OrganizationFormFields } from '@/components/organizations/OrganizationFormFields';
import { type CreateOrganizationRequest, type IOrganization } from '@/domain/entities/organization';
import type { IBusinessUser } from '@/domain/entities/user';
import { isValidOrgName, normalizeName, slugify, slugifyInput, validateSlug } from '@/lib/organization';
import {
  buildOrganizationAddressFromFields,
  createOrganizationOnboardingDraft,
  getOrganizationSharedFieldErrors,
  mergeProfileSeedIntoOrganizationOnboardingDraft,
  normalizeOrgPhone,
  type OrganizationOnboardingDraft,
} from '@/lib/organization-form';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { checkSlug, createOrganization } from '@/features/organizations/organizationsThunks';
import { runBootstrap } from '@/features/app-state';
import { selectIsSuperAdmin } from '@/features/auth/authSlice';
import {
  selectSlugChecks,
  selectSlugChecksError,
  selectSlugChecksLoading,
} from '@/features/organizations/organizationsSlice';

type OrganizationStepProps = {
  allowCreateOrg?: boolean;
  organizationID?: string;
  profileAddress?: IBusinessUser['address'];
  profileEmail?: string;
  profilePhone?: string;
  authEmail?: string | null;
  onComplete?: (org?: IOrganization) => void;

  // Wire later:
  // isSaving?: boolean;
  // onCreateOrg?: (input: Pick<IOrganization, 'name'>) => Promise<IOrganization>;
};

export default function OrganizationStep({
  allowCreateOrg,
  organizationID,
  profileAddress,
  profileEmail,
  profilePhone,
  authEmail,
  onComplete,
}: OrganizationStepProps) {
  const dispatch = useAppDispatch();
  const slugChecks = useAppSelector(selectSlugChecks);
  const slugChecksLoading = useAppSelector(selectSlugChecksLoading);
  const slugChecksError = useAppSelector(selectSlugChecksError);
  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);

  const [values, setValues] = useState<OrganizationOnboardingDraft>(() =>
    createOrganizationOnboardingDraft({
      profileAddress,
      profileEmail,
      profilePhone,
      authEmail,
    }),
  );
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    setValues((current) =>
      mergeProfileSeedIntoOrganizationOnboardingDraft(current, {
        profileAddress,
        profileEmail,
        profilePhone,
        authEmail,
      }),
    );
  }, [authEmail, profileAddress, profileEmail, profilePhone]);

  const nameError = useMemo(() => {
    if (!values.name.trim()) return null;
    return isValidOrgName(values.name) ? null : 'Organization name must be 2–80 characters.';
  }, [values.name]);

  const slugError = useMemo(() => {
    if (!values.name.trim() && !values.slug.trim()) return null;

    const { error } = validateSlug(values.slug);
    return error;
  }, [values.name, values.slug]);

  const slugKey = useMemo(() => values.slug.trim().toLowerCase(), [values.slug]);
  const slugCheckEntry = slugKey ? slugChecks[slugKey] : null;
  const slugCheckLoading = slugKey ? !!slugChecksLoading[slugKey] : false;
  const slugCheckError = slugKey ? (slugChecksError[slugKey] ?? null) : null;
  const slugAvailabilityKnown = typeof slugCheckEntry?.available === 'boolean';
  const slugIsAvailable = slugCheckEntry?.available === true;
  const slugIsTaken = slugCheckEntry?.available === false;
  const sharedFieldErrors = useMemo(() => getOrganizationSharedFieldErrors(values), [values]);
  const websiteError = sharedFieldErrors.website;
  const emailError = sharedFieldErrors.email;

  useEffect(() => {
    if (!slugKey) return;
    if (slugError) return;
    if (slugCheckEntry) return;
    if (slugCheckLoading) return;
    if (slugCheckError) return;

    const timer = setTimeout(() => {
      void dispatch(checkSlug({ slug: slugKey }));
    }, 450);

    return () => clearTimeout(timer);
  }, [dispatch, slugCheckEntry, slugCheckError, slugCheckLoading, slugError, slugKey]);

  // Already in org
  if (organizationID) {
    return (
      <Card className="border-dashed">
        <CardHeader>
          <CardTitle>Organization Setup</CardTitle>
          <CardDescription>You’re already connected to an organization.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-sm text-green-600">✅ You are already part of an organization.</div>
          <div className="text-muted-foreground text-xs">Org ID: {organizationID}</div>

          <div className="flex justify-end">
            <Button type="button" onClick={() => onComplete?.()}>
              Continue
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Not allowed to create org -> centered alert
  if (!allowCreateOrg && !isSuperAdmin) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-4">
        <Card className="w-full max-w-lg border-dashed">
          <CardHeader className="text-center">
            <div className="bg-destructive/10 mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full">
              <ShieldAlert className="text-destructive h-7 w-7" />
            </div>
            <CardTitle>Organization access required</CardTitle>
            <CardDescription>Your account is not permitted to create an organization.</CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="bg-muted rounded-md border p-4 text-sm">
              Please contact your <span className="font-medium">system administrator</span> to enable{' '}
              <span className="font-medium">Create Organization</span> for your user.
            </div>

            <div className="text-muted-foreground text-xs">
              If you already have an organization, joining/search will be added here soon.
            </div>

            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => toast.message('Contact your system admin to enable org creation.')}
              >
                OK
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Allowed to create org -> show create form
  const handleCreate = async () => {
    setCreateError(null);

    const name = normalizeName(values.name);
    if (!isValidOrgName(name)) {
      toast.error('Enter a valid organization name.');
      return;
    }
    if (slugError) {
      toast.error(slugError);
      return;
    }
    if (websiteError) {
      toast.error(websiteError);
      return;
    }
    if (emailError) {
      toast.error(emailError);
      return;
    }

    try {
      setSaving(true);
      const org: CreateOrganizationRequest = {
        title: name,
        name,
        slug: values.slug.trim(),
        website: values.website.trim() || undefined,
        email: values.email.trim() || undefined,
        phone: normalizeOrgPhone(values.phone),
        address: buildOrganizationAddressFromFields(values),
      };

      const response = await dispatch(createOrganization({ item: org })).unwrap();
      toast.success(response.message || 'Organization created.');
      setCreateError(null);
      await dispatch(runBootstrap());
      onComplete?.(response.data.organization);
    } catch (e) {
      const message =
        typeof e === 'object' && e && 'message' in e && typeof e.message === 'string' ? e.message : String(e);
      setCreateError(message);
      toast.error(`Failed to create organization: ${message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-dashed">
      <CardHeader>
        <CardTitle>Organization Setup</CardTitle>
        <CardDescription>Create your organization. It will remain inactive until admin activation.</CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Create org */}
        <div className="space-y-4 rounded-md border p-4">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            <p className="text-sm font-medium">Create an organization</p>
          </div>
          <p className="text-muted-foreground text-xs">Organization status is set to inactive by default.</p>

          <div className="space-y-2">
            <Label htmlFor="orgName">
              Organization name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="orgName"
              value={values.name}
              onChange={(e) => {
                const name = e.target.value;
                setValues((s) => ({
                  ...s,
                  name,
                  slug: slugManuallyEdited ? s.slug : slugify(name),
                }));
              }}
              placeholder="Acme Builders"
              autoComplete="organization"
              disabled={saving}
            />
            {nameError ? <p className="text-destructive text-xs">{nameError}</p> : null}
            <p className="text-muted-foreground text-xs">This will be visible to your team and used for invitations.</p>
          </div>

          <div className="space-y-2">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="orgSlug">
                  Slug <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="orgSlug"
                  value={values.slug}
                  onChange={(e) => {
                    setSlugManuallyEdited(true);
                    setValues((s) => ({ ...s, slug: slugifyInput(e.target.value) }));
                  }}
                  placeholder="acme-builders"
                  disabled={saving}
                />
                {slugError ? <p className="text-destructive text-xs">{slugError}</p> : null}
              </div>

              <div className="flex items-center pt-8 text-sm">
                {!slugKey ? (
                  <span className="text-muted-foreground">Start typing to check availability.</span>
                ) : slugError ? (
                  <>
                    <XCircle className="text-destructive mr-2 h-4 w-4 shrink-0" />
                    <span className="text-destructive">Invalid slug format.</span>
                  </>
                ) : slugCheckLoading ? (
                  <>
                    <Loader2 className="text-muted-foreground mr-2 h-4 w-4 shrink-0 animate-spin" />
                    <span className="text-muted-foreground">Checking availability…</span>
                  </>
                ) : slugCheckError ? (
                  <>
                    <XCircle className="text-destructive mr-2 h-4 w-4 shrink-0" />
                    <span className="text-destructive">Could not verify slug.</span>
                  </>
                ) : slugIsAvailable ? (
                  <>
                    <CheckCircle2 className="mr-2 h-4 w-4 shrink-0 text-green-600" />
                    <span className="text-green-600">Slug is available.</span>
                  </>
                ) : slugIsTaken ? (
                  <>
                    <XCircle className="text-destructive mr-2 h-4 w-4 shrink-0" />
                    <span className="text-destructive">Slug already exists.</span>
                  </>
                ) : (
                  <span className="text-muted-foreground">Waiting to check…</span>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <OrganizationFormFields
              values={values}
              errors={sharedFieldErrors}
              disabled={saving}
              showName={false}
              onChange={(patch) => setValues((s) => ({ ...s, ...patch }))}
            />
          </div>

          <div className="flex justify-end">
            <Button
              type="button"
              onClick={handleCreate}
              disabled={
                saving ||
                !!nameError ||
                !!slugError ||
                !!websiteError ||
                !!emailError ||
                !!slugCheckLoading ||
                !!slugCheckError ||
                !slugAvailabilityKnown ||
                !slugIsAvailable ||
                !values.name.trim()
              }
            >
              {saving ? 'Creating…' : 'Create organization'}
            </Button>
          </div>
          {createError ? (
            <div className="bg-destructive text-destructive-foreground border-destructive/80 flex items-start gap-2 rounded-md border px-3 py-2 text-xs">
              <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{createError}</span>
            </div>
          ) : null}
        </div>

        {/* Join org (later) */}
        <div className="bg-muted/40 rounded-md border p-4">
          <p className="text-sm font-medium">Join an existing organization</p>
          <p className="text-muted-foreground mt-1 text-xs">
            Coming soon: search by org name/domain, or join via invitation link.
          </p>

          <div className="mt-3 flex justify-end">
            <Button type="button" variant="outline" disabled>
              Search organizations (soon)
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
